import { useCallback, useState } from 'react';
import { UploadCloud, X, Star } from 'lucide-react';
import { TID } from '@yalidavet/shared';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';

export interface UploadedPhoto {
  id: string;
  url: string;
  name: string;
}

interface Props {
  photos: UploadedPhoto[];
  onChange: (photos: UploadedPhoto[]) => void;
}

const MAX_PHOTOS = 20;

export default function PhotoDropzone({ photos, onChange }: Props) {
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFiles = useCallback(
    async (fileList: FileList | null) => {
      if (!fileList?.length) return;
      const files = Array.from(fileList).filter((f) => f.type.startsWith('image/'));
      if (!files.length) {
        setError('Sadece görsel dosyaları yükleyebilirsiniz.');
        return;
      }
      if (photos.length + files.length > MAX_PHOTOS) {
        setError(`En fazla ${MAX_PHOTOS} fotoğraf yükleyebilirsiniz.`);
        return;
      }
      setError(null);
      setUploading(true);
      try {
        const res = await api.uploadPhotos(files);
        onChange([...photos, ...res.items]);
      } catch (e: any) {
        setError(e?.message ?? 'Fotoğraflar yüklenemedi.');
      } finally {
        setUploading(false);
      }
    },
    [photos, onChange],
  );

  const remove = (id: string) => onChange(photos.filter((p) => p.id !== id));
  const makeCover = (id: string) => {
    const target = photos.find((p) => p.id === id);
    if (!target) return;
    onChange([target, ...photos.filter((p) => p.id !== id)]);
  };

  return (
    <div className="space-y-4">
      <div
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => { e.preventDefault(); setDragging(false); void handleFiles(e.dataTransfer.files); }}
        className={cn(
          'flex flex-col items-center justify-center gap-3 rounded-xl2 border-2 border-dashed p-10 text-center transition-colors',
          dragging ? 'border-bosphorus-500 bg-bosphorus-50' : 'border-ink-200 bg-ink-50/40',
        )}
        data-testid={TID.hostDropzone}
        data-dragging={dragging}
      >
        <UploadCloud className="h-10 w-10 text-ink-400" aria-hidden />
        <div>
          <p className="font-semibold text-ink-900">Fotoğrafları buraya sürükleyin</p>
          <p className="text-sm text-ink-500">JPG, PNG veya WebP · en fazla 12 MB · en az 5 fotoğraf önerilir</p>
        </div>
        <label className="yd-btn-secondary cursor-pointer">
          Bilgisayardan Seç
          <input
            type="file"
            accept="image/*"
            multiple
            className="sr-only"
            onChange={(e) => void handleFiles(e.target.files)}
            data-testid="host-photo-input"
          />
        </label>
        {uploading && <p className="text-sm text-bosphorus-700" data-testid="host-photo-uploading">Yükleniyor…</p>}
      </div>

      {error && (
        <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700" role="alert" data-testid="host-photo-error">
          {error}
        </p>
      )}

      {photos.length > 0 && (
        <ul className="grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid={TID.hostPhotoPreview} data-count={photos.length}>
          {photos.map((p, i) => (
            <li key={p.id} className="group relative aspect-[4/3] overflow-hidden rounded-lg bg-ink-100">
              <img src={p.url} alt={p.name} className="h-full w-full object-cover" />
              {i === 0 && (
                <span className="absolute left-2 top-2 rounded bg-bosphorus-800 px-1.5 py-0.5 text-[10px] font-semibold text-white">
                  Kapak
                </span>
              )}
              <div className="absolute inset-x-0 bottom-0 flex justify-end gap-1 bg-gradient-to-t from-black/60 to-transparent p-2 opacity-0 transition-opacity group-hover:opacity-100">
                {i !== 0 && (
                  <button
                    type="button"
                    onClick={() => makeCover(p.id)}
                    className="rounded bg-white/90 p-1.5 hover:bg-white"
                    aria-label="Kapak fotoğrafı yap"
                    data-testid={`host-photo-cover-${p.id}`}
                  >
                    <Star className="h-3.5 w-3.5" />
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => remove(p.id)}
                  className="rounded bg-white/90 p-1.5 hover:bg-white"
                  aria-label="Fotoğrafı kaldır"
                  data-testid={`host-photo-remove-${p.id}`}
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
